import { ColorPaletteKind } from '@chronosphereio/viz-framework-core';

// Source of truth for all colors can be found at http://go/time-series-color-palette
export interface ColorPalette {
  /**
   * Categorical defines the categorical palette to be used. These colors will be picked in order
   * and looped over.
   */
  Categorical: string[];

  /**
   * A more limited variation of the `Categorical` palette that removes reds and oranges, which is
   * useful in use cases where those colors want to be reserved for things like errors or warnings.
   *
   * This palette option is not currently exposed in the dashboards editor options.
   */
  CategoricalNoErrorColors: string[];

  /**
   * A more limited variation of the `Categorical` palette that removes status colors, which is
   * useful in use cases where those colors want to be reserved for things like successes, errors,
   * or warnings.
   */
  CategoricalNoStatusColors: string[];

  /**
   * Consistent defines the consistent palette to be used. These will be assigned to series
   * by name in the order they are defined
   */
  Consistent: string[];

  /**
   * SpectrumConsistent defines how we present the consistent palette in our palette options.
   * Since we alternate hues in Consistent for technical reason, this provides a more aesthetically
   * pleasing ordering.
   */
  SpectrumConsistent: string[];
}

// Similar to Grafana's palette with slight modifications, see sc-92179
const DEFAULT_PALETTE = [
  '#5BBE76',
  '#EAB839',
  '#6ED0E0',
  '#EF843C',
  '#1F78C1',
  '#D14949',
  '#C25CCB',
  '#705DA0',
  '#508642',
  '#CCA300',
  '#447EBC',
  '#C15C17',
  '#890F02',
  '#0E4B88',
  '#872779',
  '#584477',
  '#B7DBAB',
  '#F4D495',
  '#98D4DE',
  '#E7A67A',
  '#F29191',
  '#82B5D8',
  '#D990D5',
  '#998CCB',
];

// Version of DEFAULT_PALETTE without red/orange hues.
const DEFAULT_PALETTE_LIMITED = [
  '#5BBE76',
  '#EAB839',
  '#6ED0E0',
  '#1F78C1',
  '#C25CCB',
  '#705DA0',
  '#508642',
  '#CCA300',
  '#447EBC',
  '#0E4B88',
  '#872779',
  '#584477',
  '#B7DBAB',
  '#F4D495',
  '#98D4DE',
  '#82B5D8',
  '#D990D5',
  '#998CCB',
];

// Version of DEFAULT_PALETTE without status colors.
const DEFAULT_PALETTE_NO_STATUS_COLORS = [
  '#EAB839',
  '#6ED0E0',
  '#1F78C1',
  '#C25CCB',
  '#894700',
  '#705DA0',
  '#CCA300',
  '#447EBC',
  '#0E4B88',
  '#872779',
  '#00617E',
  '#584477',
  '#B09273',
  '#F4D495',
  '#98D4DE',
  '#82B5D8',
  '#D990D5',
  '#998CCB',
];

export const LIGHT_COLOR_PALETTE: ColorPalette = {
  Categorical: DEFAULT_PALETTE,
  CategoricalNoErrorColors: DEFAULT_PALETTE_LIMITED,
  CategoricalNoStatusColors: DEFAULT_PALETTE_NO_STATUS_COLORS,
  Consistent: [
    '#005C99',
    '#5BB982',
    '#9C597E',
    '#DEC554',
    '#CC4E00',
    '#FA97A3',
    '#2C8BC8',
    '#A7C44F',
    '#A486CB',
    '#715B09',
    '#E98649',
    '#609702',
    '#CC8EB1',
    '#603597',
    '#8DC6EC',
    '#1C7D45',
    '#A8870E',
    '#F7B06E',
    '#A01828',
    '#D8C6F1',
  ],
  SpectrumConsistent: [
    '#005C99',
    '#2C8BC8',
    '#8DC6EC',
    '#1C7D45',
    '#5BB982',
    '#609702',
    '#A7C44F',
    '#715B09',
    '#A8870E',
    '#DEC554',
    '#CC4E00',
    '#E98649',
    '#F7B06E',
    '#A01828',
    '#FA97A3',
    '#9C597E',
    '#CC8EB1',
    '#603597',
    '#A486CB',
    '#D8C6F1',
  ],
};

export const DARK_COLOR_PALETTE: ColorPalette = {
  Categorical: DEFAULT_PALETTE,
  CategoricalNoErrorColors: DEFAULT_PALETTE_LIMITED,
  CategoricalNoStatusColors: DEFAULT_PALETTE_NO_STATUS_COLORS,
  Consistent: [
    '#ADDEFF',
    '#297A4B',
    '#CF96B6',
    '#7A620A',
    '#F29B4A',
    '#932532',
    '#50A2D8',
    '#5B8415',
    '#A486CB',
    '#EAD27B',
    '#D96A26',
    '#A0BF40',
    '#8F5273',
    '#CEBAE8',
    '#0068AD',
    '#5DB683',
    '#C69F10',
    '#A13F02',
    '#F0A1AB',
    '#704BA0',
  ],
  SpectrumConsistent: [
    '#A1CFED',
    '#56A1D2',
    '#0068AD',
    '#67AD84',
    '#267346',
    '#A0BF40',
    '#618D16',
    '#DAC36C',
    '#C69F10',
    '#7A620A',
    '#E09B5C',
    '#D66B29',
    '#A13F02',
    '#DD7E89',
    '#932532',
    '#C992B1',
    '#8F5273',
    '#C9B6E2',
    '#A489C8',
    '#704BA0',
  ],
};

export interface SeriesColorProps {
  kind?: ColorPaletteKind;
  primaryColor: string;
  seriesName: string;
  seriesIndex: number;
}

export interface SeriesColorGeneratorProps {
  palette: ColorPalette;
}

export class SeriesColorGenerator {
  readonly palette: ColorPalette = LIGHT_COLOR_PALETTE;

  private nextConsistentSeriesIndex: number = 0;
  private consistentSeriesNameToColor: Record<string, string> = {};

  constructor(props: SeriesColorGeneratorProps) {
    const { palette } = props;

    if (palette) {
      this.palette = palette;
    }
  }

  getSeriesColor(props: SeriesColorProps) {
    const { primaryColor, seriesName, seriesIndex, kind = '' } = props;
    const categoricalPalette = this.palette.Categorical;
    // Fallback is unlikely to set unless echarts theme palette in charts theme provider is undefined.
    const fallbackColor: string = categoricalPalette[0] ? categoricalPalette[0] : primaryColor;

    if (kind === 'Consistent') {
      return this.getConsistentSeriesNameColor(seriesName, fallbackColor);
    }

    if (kind === 'CategoricalNoErrorColors') {
      return this.getCategoricalPaletteColor(seriesIndex, fallbackColor, this.palette.CategoricalNoErrorColors);
    }

    if (kind === 'CategoricalNoStatusColors') {
      return this.getCategoricalPaletteColor(seriesIndex, fallbackColor, this.palette.CategoricalNoStatusColors);
    }

    return this.getCategoricalPaletteColor(seriesIndex, fallbackColor);
  }

  // getConsistentSeriesNameColor uses a predefined color palette to pick what color a series will be.
  // We step through the palette to pick colors, unless the series name already has a color assigned.
  getConsistentSeriesNameColor(inputString: string, fallbackColor: string) {
    const hash: string = inputString || '';
    const consistentPalette = this.palette.Consistent;
    if (!this.consistentSeriesNameToColor[hash]) {
      // N.B. by preincrementing, we can ensure that there's atomicity between concurrent usage.
      const index = this.nextConsistentSeriesIndex++ % consistentPalette.length;
      const color = consistentPalette[index]!;
      this.consistentSeriesNameToColor[hash] = color;
      return color;
    }
    return this.consistentSeriesNameToColor[hash] ?? fallbackColor;
  }

  /**
   * Default classical qualitative palette that cycles through the colors array by index.
   */
  getCategoricalPaletteColor(
    seriesIndex: number,
    fallbackColor: string,
    categoricalPalette = this.palette.Categorical
  ): string {
    if (categoricalPalette.length === 0) {
      return fallbackColor;
    }
    // Loop through predefined static color palette
    const paletteIndex = seriesIndex % categoricalPalette.length;
    // fallback color comes from echarts theme
    const seriesColor = categoricalPalette[paletteIndex] ?? fallbackColor;
    return seriesColor;
  }
}
